import React from 'react';
import { connect } from 'react-redux';
import { compose } from 'redux';
import { formValueSelector, reduxForm } from 'redux-form';
import { pending } from 'redux-saga-thunk';
import { withRouter } from 'react-router';
import get from 'lodash/get';
import keyBy from 'lodash/keyBy';
import endsWith from 'lodash/endsWith';
import moment from 'moment/moment';
import startsWith from 'lodash/startsWith';
import { fromEntities, fromResource } from '../../store/selectors';
import ForecastResults from '../../components/molecules/ForecastResults/index';
import { resourceUpdateRequest } from '../../store/resource/actions';

const ForecastContainer = props => <ForecastResults {...props} />;

const selectForecast = formValueSelector('Forecast');
const selectResult = formValueSelector('ForecastResults');
const selectAdjustment = formValueSelector('ForecastAdjustments');

const CPEOrCPM = ({ title }) => {
  if (!title) return '';
  const stringTitle = (typeof title === 'string' ? title : title.props.children).toLowerCase();
  const cpm = startsWith(stringTitle, 'precep') || startsWith(stringTitle, 'audience:');
  return cpm ? 'CPM' : 'CPE';
};

const mapStateToProps = (
  { modal, ...state },
  {
    match: {
      params: { forecastId, revisionId },
    },
  }
) => {
  const forecastMain = fromEntities.getDetail(state, 'forecast', forecastId) || {};
  const audienceListEntity = fromEntities.getList(
    state,
    'forecastRevisionAudience',
    fromResource.getList(state, 'forecastRevisionAudience')
  );
  const revision = fromEntities.getDetail(state, 'forecastRevision', revisionId) || {};
  const revisionList =
    fromEntities.getList(
      state,
      'forecastRevision',
      fromResource.getList(state, 'forecastRevision')
    ) || [];

  const forecast = {
    ...forecastMain,
    ...revision,
    end_date: selectForecast(state, 'end_date'),
    start_date: selectForecast(state, 'start_date'),
  };
  const location = get(state, `resource.forecastRevisionChain.count`, 0);
  const chain = get(state, `resource.forecastRevisionLocation.count`, 0);

  const audienceList = audienceListEntity.map(a => ({
    id: a.id,
    included: a.included,
    title: `Audience: ${a.name}`,
    adjFactor: a.adjust_factor * 100,
    dailyIE: a.daily_impressions,
    totalIE: 0,
    cost: a.cpm,
    totalNet: 0,
  }));

  const defaultPlacementList = [
    {
      included: forecast.instore_included,
      title: 'inStore',
      adjFactor: forecast.instore_adj_factor * 100,
      dailyIE: forecast.instore_daily_impressions,
      totalIE: null,
      cost: forecast.instore_cpe,
      totalNet: null,
    },
    {
      included: forecast.inhand_included,
      title: 'inHand',
      adjFactor: forecast.inhand_adj_factor * 100,
      dailyIE: forecast.inhand_daily_impressions,
      totalIE: null,
      cost: forecast.inhand_cpe,
      totalNet: null,
    },
    {
      included: forecast.inpath_included,
      title: 'inPath',
      adjFactor: forecast.instore_adj_factor * 100,
      dailyIE: forecast.preceptivity_daily_impressions,
      totalIE: null,
      cost: forecast.inpath_cpe,
      totalNet: null,
    },
  ];

  const preceptivity = [
    {
      included: forecast.preceptivity_included,
      title: 'Preceptivity',
      adjFactor: forecast.preceptivity_adj_factor * 100,
      dailyIE: forecast.preceptivity_daily_impressions,
      totalIE: null,
      cost: forecast.preceptivity_cpm,
      totalNet: null,
    },
  ];

  const forecastLength = Math.ceil(
    moment.duration(moment(forecast.end_date).diff(moment(forecast.start_date))).asDays()
  );

  const formulas = {};
  formulas.calcInhandDaily = r =>
    r.dailyIE *
    (selectResult(state, 'inhand_adj_factor') / 100) *
    selectAdjustment(state, 'inhand_product_count') *
    (selectAdjustment(state, 'ease_of_scan') / 100);
  formulas.calcInpathDaily = r =>
    r.dailyIE *
    (selectResult(state, 'instore_adj_factor') / 100) *
    selectAdjustment(state, 'avg_ad_request_per_device') *
    (selectAdjustment(state, 'inpath_pct_of_requests') / 100);
  formulas.calcPreceptivityDaily = r =>
    r.dailyIE *
    (selectResult(state, 'preceptivity_adj_factor') / 100) *
    selectAdjustment(state, 'avg_ad_request_per_device');
  formulas.calcDailyIE = r => {
    let response = 0;
    if (!r.dailyIE) return response;

    if (r.title === 'inPath') {
      response = formulas.calcInpathDaily(r);
    } else if (r.title === 'inHand') {
      response = formulas.calcInhandDaily(r);
    } else if (r.title === 'Preceptivity') {
      response = formulas.calcPreceptivityDaily(r);
    } else {
      const adj = r.id ? `audience[${r.id}][adjFactor]` : `${r.title.toLowerCase()}_adj_factor`;
      response = r.dailyIE * (selectResult(state, adj) / 100);
    }
    return Math.round(response);
  };
  formulas.calcTotalIE = r => Math.round(formulas.calcDailyIE(r) * forecastLength);
  formulas.calcTotalNetCost = r => {
    const totalIE = Number(formulas.calcTotalIE(r));
    const costField = r.id
      ? `audience[${r.id}][cost]`
      : `${r.title.toLowerCase()}_${CPEOrCPM(r).toLowerCase()}`;
    const cost = Number(selectResult(state, costField));

    if (Number.isNaN(cost) || !cost) return 0;

    if (CPEOrCPM(r) === 'CPM') {
      const newVal = (totalIE / 1000) * cost;
      if (!Number.isNaN(newVal)) {
        return `${Math.ceil(newVal).toFixed(2)}`;
      }
    }

    return Math.ceil(Number(totalIE * cost));
  };

  formulas.calcCPM = (r, n) => n;

  const totalMoments = [
    {
      included: null,
      title: <b>Total Moments</b>,
      adjFactor: null,
      dailyIE: 0,
      totalIE: 0,
      cost: null,
      totalNet: 0,
    },
  ];

  const finalTotals = [
    {
      included: null,
      title: <b>Totals</b>,
      adjFactor: null,
      dailyIE: 0,
      totalIE: 0,
      cost: null,
      totalNet: 0,
    },
  ];

  defaultPlacementList.forEach(r => {
    const includeSelect = r.id
      ? `audience[${r.id}][included]`
      : `${r.title.toLowerCase()}_included`;
    const included = selectResult(state, includeSelect);

    if (r.title && typeof r.title === 'string' && !!included) {
      totalMoments[0].dailyIE += Number(formulas.calcDailyIE(r));
      totalMoments[0].totalIE += Number(formulas.calcTotalIE(r));
      totalMoments[0].totalNet += Number(formulas.calcTotalNetCost(r));
    }
  });

  [...defaultPlacementList, ...preceptivity, ...audienceList].forEach(r => {
    const includeSelect = r.id
      ? `audience[${r.id}][included]`
      : `${r.title.toLowerCase()}_included`;
    const included = selectResult(state, includeSelect);

    if (r.title && typeof r.title === 'string' && !!included) {
      finalTotals[0].dailyIE += Number(formulas.calcDailyIE(r));
      finalTotals[0].totalIE += Number(formulas.calcTotalIE(r));
      finalTotals[0].totalNet += Number(formulas.calcTotalNetCost(r));
    }
  });

  // Multiply forecast adjust factor by 100 so it displays as a full percent in the form
  // We don't use redux-form parse / format props because they screw up the typing of values
  // So instead, we convert on load and on save
  Object.keys(forecast).forEach(k => {
    if (endsWith(k, 'adj_factor')) {
      forecast[k] *= 100;
    }
  });

  return {
    ...formulas,
    CPEOrCPM,
    resultsData: [
      ...defaultPlacementList,
      ...totalMoments,
      ...preceptivity,
      ...audienceList,
      ...finalTotals,
    ],
    hasLocationOrChain: !!(location || chain),
    revisionList: revisionList.map(r => ({ label: r.start_date, value: r.revision_id })),
    forecast,
    loading: pending(state, 'forecastDetailRead'),
    initialValues: {
      ...forecast,
      audience: keyBy(audienceList, 'id'),
      revision: `${revisionId}`,
    },
  };
};

const mapDispatchToProps = (
  dispatch,
  {
    match: {
      params: { forecastId, revisionId },
    },
  }
) => ({
  updateForecast: data =>
    dispatch(
      resourceUpdateRequest(
        'forecast/:forecastId/revision/:id',
        { id: revisionId, forecastId },
        data
      )
    ),
});

export default compose(
  withRouter,
  connect(
    mapStateToProps,
    mapDispatchToProps
  ),
  reduxForm({
    form: 'ForecastResults',
    enableReinitialize: true,
  })
)(ForecastContainer);
